// redux/restaurantSlice.js

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { userRequest } from "../api/requestMethods";  // Assuming userRequest is set up correctly to handle API requests
import {
    MAIN,
    applyBranchEventToSections,
    branchEventIsFor,
} from "../utils/branchAvailability";

const initialState = {
    restaurantData: [],
    optionGroups: [],
    isFetching: false,
    error: null,
    selectedRestaurant: null,
    details: null,
    // The request id of the latest details read; an older answer is dropped.
    detailsReadId: null,
};

export const fetchRestaurants = createAsyncThunk(
    "restaurants/fetchRestaurants",
    async (_, { rejectWithValue }) => {
        try {
            const response = await userRequest.get("/restaurants");
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response.data);
        }
    }
);

/* One location's menu sections, read without touching this slice: the
   storefront's thunk below and the Order together basket, which shows the
   basket's restaurant beside whatever storefront the slice holds. */
export const requestMenuSections = async ({ vendorId, outletId = null }) => {
    const response = await userRequest.get("/restaurants/menu-sections", {
        params: { vendorId, outletId: outletId || MAIN },
    });
    return response.data;
};

/* The menu at one location (4 Oct 2026): a bare restaurant id (the main
   branch) or { restaurantId, outletId }. The read names the location, so
   each dish comes back as it stands there: sold out or switched off there,
   the price there, and a dish a curated branch menu leaves out is left out.
   This replaces reading the parent's menu for every branch (the old TODO). */
export const fetchRestaurantDetails = createAsyncThunk(
    "restaurants/fetchRestaurantDetails",
    async (arg, { rejectWithValue }) => {
        const { restaurantId, outletId = null } =
            arg && typeof arg === "object" ? arg : { restaurantId: arg };
        try {
            const sections = await requestMenuSections({ vendorId: restaurantId, outletId });
            return {
                restaurantId,
                outletId: outletId || null,
                sections,
                optionGroups: sections.optionGroups,
            };
        } catch (error) {
            return rejectWithValue(error.response?.data || "Failed to fetch the menu");
        }
    }
);

export const fetchSingleRestaurant = createAsyncThunk(
    "restaurants/fetchSingleRestaurant",
    async (restaurantId, { rejectWithValue }) => {
        try {
            // Use the restaurant-profile endpoint which returns the raw document
            // with the correct _id (the /restaurants?vendorId= endpoint groups by
            // owner, which changes the _id to the owner's user ID)
            const response = await userRequest.get(`/restaurants/restaurant-profile/${restaurantId}`);
            if (!response.data) {
                return rejectWithValue("Restaurant not found");
            }
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || "Failed to fetch restaurant");
        }
    }
);

const restaurantSlice = createSlice({
    name: "restaurants",
    initialState,
    reducers: {
        resetOptionGroups: (state) => {
            state.optionGroups = [];
        },
        setSelectedRestaurant: (state, action) => {
            state.selectedRestaurant = action.payload;
        },
        clearRestaurantDetails: (state) => {
            state.details = null;
            state.selectedRestaurant = null;
        },
        /* Sold out for the day, live. The server broadcasts
           `product:availabilityChanged` when a dish reaches its daily plate
           limit, when the vendor marks it sold out or puts it back, and when
           the business day rolls over. The dish stays in the section tree;
           only its flags change, so the open storefront flips in place. */
        applyMenuAvailabilityChange: (state, action) => {
            const payload = action.payload || {};
            const productId = String(payload.productId || "");
            const sections = state.details?.sections;
            if (!productId || !Array.isArray(sections)) return;
            for (const section of sections) {
                for (const product of section?.products || []) {
                    if (String(product._id) !== productId) continue;
                    product.soldOutToday = payload.soldOutToday === true;
                    product.soldOutReason = payload.soldOutReason ?? null;
                    product.platesLeftToday = payload.platesLeftToday ?? null;
                    product.todaysLimit = payload.todaysLimit ?? product.todaysLimit ?? null;
                    product.dailyLimit = payload.dailyLimit ?? product.dailyLimit ?? null;
                }
            }
        },
        /* Realtime `branchProduct:changed` (4 Oct 2026): a branch switched a
           dish on or off, or sold it out, at one location. Patched in place
           on the open menu (the badge where the menu was read, `notAt` for
           "Get it from"); the legacy `soldOutToday` stays the product-level
           flag `product:availabilityChanged` owns. */
        applyBranchProductChange: (state, action) => {
            const payload = action.payload || {};
            const sections = state.details?.sections;
            const restaurant = state.selectedRestaurant;
            const ids = [state.details?.restaurantId, restaurant?._id, restaurant?.owner];
            if (!Array.isArray(sections) || !branchEventIsFor(payload, ids)) return;
            applyBranchEventToSections(sections, payload);
        },
        /* A vendor edited their opening hours. The server sends this as a
           DELTA on `catalog:public`, deliberately not as a reason to refetch:
           hours are edited constantly and by every vendor, so a refetch per
           edit would have every connected customer re-request the whole
           restaurant list whenever any one vendor touched their schedule.

           The payload's `restaurantId` is the id of the thing whose hours
           moved, which for `isOutlet` is the OUTLET's own id, with the parent
           in `parentRestaurantId`. */
        applyRestaurantHoursChange: (state, action) => {
            const payload = action.payload || {};
            const changedId = String(payload.restaurantId || "");
            if (!changedId || !Array.isArray(state.restaurantData)) return;

            const patch = (target) => {
                if (payload.isOpen !== undefined) target.isOpen = payload.isOpen;
                if (payload.nextOpening !== undefined) target.nextOpening = payload.nextOpening;
                if (payload.openHours !== undefined) target.openHours = payload.openHours;
                if (payload.specialHours !== undefined) target.specialHours = payload.specialHours;
            };

            for (const restaurant of state.restaurantData) {
                if (String(restaurant._id) === changedId) {
                    patch(restaurant);
                    return;
                }
                const outlet = (restaurant.outlets || []).find(
                    (o) => String(o._id) === changedId,
                );
                if (outlet) {
                    patch(outlet);
                    return;
                }
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchRestaurants.pending, (state) => {
                state.isFetching = true;
            })
            .addCase(fetchRestaurants.fulfilled, (state, action) => {
                state.restaurantData = action.payload.restaurants;
                state.isFetching = false;
                state.error = null;
            })
            .addCase(fetchRestaurants.rejected, (state, action) => {
                state.error = action.payload;
                state.isFetching = false;
            })
            // The LATEST menu read wins (4 Oct 2026): switching branch while
            // a read is in flight must not leave the old branch's menu up.
            .addCase(fetchRestaurantDetails.pending, (state, action) => {
                state.isFetching = true;
                state.detailsReadId = action.meta?.requestId || null;
            })
            .addCase(fetchRestaurantDetails.fulfilled, (state, action) => {
                if (state.detailsReadId && action.meta?.requestId !== state.detailsReadId) return;
                const { restaurantId, outletId, sections, optionGroups } = action.payload;
                state.details = { restaurantId, outletId, sections, optionGroups };
                state.isFetching = false;
                state.error = null;
            })
            .addCase(fetchRestaurantDetails.rejected, (state, action) => {
                if (state.detailsReadId && action.meta?.requestId !== state.detailsReadId) return;
                state.error = action.payload;
                state.isFetching = false;
            })
            .addCase(fetchSingleRestaurant.pending, (state) => {
                state.isFetching = true;
            })
            .addCase(fetchSingleRestaurant.fulfilled, (state, action) => {
                state.selectedRestaurant = action.payload;
                state.isFetching = false;
                state.error = null;
            })
            .addCase(fetchSingleRestaurant.rejected, (state, action) => {
                state.error = action.payload;
                state.isFetching = false;
            });
    },
});

export const {
    resetOptionGroups,
    setSelectedRestaurant,
    clearRestaurantDetails,
    applyMenuAvailabilityChange,
    applyRestaurantHoursChange,
    applyBranchProductChange,
} = restaurantSlice.actions;
export default restaurantSlice.reducer;
